<template>
  <div>
    <Alert variant="info" v-if="!alerts.newPortal.dismissed" @close="clearPortalAlert">
      🎉 Welcome to your new portal experience!
      <a href="http://help.nexcess.net/en_US/portal-updates" target="_blank" class="text-white">
        Click here to learn what's new
      </a>
    </Alert>
    <PageTitle :title="$t('global.home')" />
    <div class="row">
      <div class="col-lg-8">
        <div class="row">
          <div class="col-lg-6 pb-4">
            <Card :title="$t('global.call-in-pin')" :icons="callInPinIcons">
              <div class="mb-2">
                {{ $t("global.account-id") }}: {{ client.id }}
                <CopyToClipboard :text="client.id.toString()" />
              </div>
              <div class="pin-container d-inline-flex">
                {{ $t("global.pin") }}: {{ pin }}
                <div :class="showPin ? 'ml-1' : ''">
                  <CopyToClipboard v-if="showPin" :text="pin.toString()" />
                </div>
                <div class="ml-2" @click="toggleShowPin">
                  <Icon
                    class="iconToggle"
                    :icon="showPin ? 'hide' : 'view'"
                    :size="20"
                    color="epsilon"
                  />
                </div>
              </div>
            </Card>
          </div>
          <div class="col-lg-6 pb-4">
            <Card :title="$t('global.support')" :icons="supportIcons">
              <EmptyState
                v-if="client.activeTicketCount === 0"
                :bold-message="$t('global.no-tickets-bold-message')"
                image="tickets"
                :message="$t('global.no-tickets-message')"
              />
              <CardDataHighlight
                v-else-if="client.activeTicketCount"
                :subtitle="$t('global.active-tickets')"
                :data="client.activeTicketCount"
              />
              <ErrorState v-else />
            </Card>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 pb-4">
            <Card :title="$t('global.invoices')" :icons="invoicesIcons">
              <CardDataHighlight
                :title="isOverdue() ? $t('global.overdue') : $t('global.due')"
                :subtitle="isOverdue() ? $t('global.total-amount-due', { amount: client.due }) : ''"
                :color="getPaymentClass()"
              >
                <template #data>
                  <div>{{ moneyFormat(isOverdue() ? client.overdue : client.due) }}</div>
                </template>
                <template v-if="isOverdue()" #subtitle>
                  <div class="d-inline-flex">
                    <div class="mr-2">{{ $t("global.total-amount-due") }}:</div>
                    <div>
                      <div>{{ moneyFormat(client.due) }}</div>
                    </div>
                  </div>
                </template>
              </CardDataHighlight>
            </Card>
          </div>
          <div class="col-lg-6 pb-4">
            <Card :title="$t('global.credit')" :icons="creditIcons">
              <CardDataHighlight>
                <template #data>
                  <div>{{ moneyFormat(client.credits) }}</div>
                </template>
              </CardDataHighlight>
            </Card>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 pb-4">
            <Card :title="$t('global.default-payment')" :icons="defaultPaymentIcons">
              <div class="font-weight-bold mb-2">{{ $t("global." + client.paymentType) }}</div>
              <div v-if="client.paymentType === 'cc'" class="text-black-50">
                {{ client.creditCardNumber }}
              </div>
            </Card>
          </div>
          <div class="col-lg-6 pb-4">
            <Card :title="$t('global.billing-address')" :icons="addressesIcons">
              <LoadingState v-if="billingAddressStatus.isLoading" />
              <template v-else-if="billingAddressStatus.isLoaded">
                <CardBlockList
                  v-if="billingAddress"
                  :title="`${billingAddress.firstName} ${billingAddress.lastName}`"
                  :list="[
                    { item: billingAddress.email },
                    { item: billingAddress.address1 },
                    { item: billingAddress.address2 },
                    {
                      item: `${billingAddress.city}, ${billingAddress.state} ${billingAddress.zip}`,
                    },
                    { item: billingAddress.country },
                  ]"
                  :noBorder="true"
                />
                <EmptyState v-else :message="$t('global.no-billing-address')" image="address" />
              </template>
              <ErrorState v-else />
            </Card>
          </div>
        </div>
      </div>
      <div class="col-lg-4 pb-4">
        <Card :title="$t('global.announcements')" :icons="announcementsIcons">
          <AnnouncementsDigest :announcements="announcements" />
        </Card>
      </div>
      <!--      Temporarily Disabling Status Section due to CSP errors-->
      <!--      <div class="col-lg-4 pb-4">-->
      <!--        <Card :title="$t('global.status')" :icons="statusIcons" class="status-page">-->
      <!--          <LoadingState v-if="!statusPageSummary" />-->
      <!--          <template v-else-if="statusPageSummary">-->
      <!--            <div class="toast">-->
      <!--              <div-->
      <!--                v-if="statusPageSummary.status.indicator === 'none'"-->
      <!--                class="toast-header toast-header-variant-success"-->
      <!--              >-->
      <!--                <Icon icon="success" :size="20" class="toast-icon" color="sigma" />-->
      <!--                {{ statusPageSummary.status.description }}-->
      <!--              </div>-->
      <!--              <div v-else class="toast-header toast-header-variant-error">-->
      <!--                <Icon icon="error" :size="20" class="toast-icon" color="tau" />-->
      <!--                {{ statusPageSummary.status.description }}-->
      <!--              </div>-->
      <!--            </div>-->
      <!--            <CardCollapse-->
      <!--              v-for="(group, groupIndex) in statusPageSummary.components.filter(-->
      <!--                (item) => item.group-->
      <!--              )"-->
      <!--              :key="groupIndex"-->
      <!--              :title="group.name"-->
      <!--            >-->
      <!--              <ul>-->
      <!--                <li-->
      <!--                  v-for="(component, index) in statusPageSummary.components.filter((item) =>-->
      <!--                    group.components.includes(item.id)-->
      <!--                  )"-->
      <!--                  :key="index"-->
      <!--                >-->
      <!--                  {{ component.name }}-->
      <!--                  <Icon-->
      <!--                    v-if="component.status === 'operational'"-->
      <!--                    icon="success"-->
      <!--                    :size="14"-->
      <!--                    class="toast-icon float-right"-->
      <!--                    color="sigma"-->
      <!--                  />-->
      <!--                  <Icon-->
      <!--                    v-if="component.status !== 'operational'"-->
      <!--                    icon="error"-->
      <!--                    :size="14"-->
      <!--                    class="toast-icon float-right"-->
      <!--                    color="tau"-->
      <!--                  />-->
      <!--                </li>-->
      <!--              </ul>-->
      <!--            </CardCollapse>-->
      <!--          </template>-->
      <!--        </Card>-->
      <!--      </div>-->
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import AnnouncementsDigest from "@/components/display/AnnouncementsDigest";
import Card from "@/components/display/Card";
import CardBlockList from "@/components/display/card-sub-components/CardBlockList";
// import CardCollapse from "@/components/display/card-sub-components/CardCollapse"; Temporarily Disabling Status Section due to CSP errors
import CardDataHighlight from "@/components/display/card-sub-components/CardDataHighlight";
import ContactSupport from "@/components/smart/modals/ContactSupport";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import DefaultPaymentEdit from "@/components/smart/modals/DefaultPaymentEdit";
import EmptyState from "@/components/display/empty-state/EmptyState";
import ErrorState from "@/components/display/ErrorState";
// import { getStatusPageSummary } from "@/utils/statusPage"; Temporarily Disabling Status Section due to CSP errors
import Icon from "@/components/display/icon/Icon";
import LoadingState from "@/components/display/LoadingState";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import NavLink from "@/classes/NavLink";
import PageTitle from "@/components/display/PageTitle";
import PinEdit from "@/components/smart/modals/PinEdit";
import { showModal as showVerifyBillingTaxModal } from "@/utils/verifyBillingTaxModal";
import store from "@/store";

export default {
  name: "HomePage",
  components: {
    Alert,
    AnnouncementsDigest,
    Card,
    CardBlockList,
    // CardCollapse, Temporarily Disabling Status Section due to CSP errors
    CardDataHighlight,
    CopyToClipboard,
    EmptyState,
    ErrorState,
    Icon,
    LoadingState,
    PageTitle,
  },
  setup(props, { root }) {
    // Temporarily Disabling Status Section due to CSP errors
    // const statusPageSummary = ref(null);

    const alerts = computed(() => store.state.alerts.list);

    const clearPortalAlert = () => {
      store.dispatch("alerts/dismiss", "newPortal");
    };

    const announcements = computed(() => store.state.announcements.list);

    const addressesIcons = [
      new NavLink("Open", "open-page", "payment-info", false, null, "epsilon"),
    ];

    const announcementsIcons = [
      new NavLink("Open", "open-page", "announcements", false, null, "epsilon"),
    ];

    const callInPinIcons = [
      new NavLink(
        "Edit",
        "edit",
        function () {
          editPin();
        },
        false,
        null,
        "epsilon"
      ),
    ];

    const defaultPaymentIcons = [
      new NavLink(
        "Edit",
        "edit",
        function () {
          editDefaultPayment();
        },
        false,
        null,
        "epsilon"
      ),
    ];

    const supportIcons = [
      new NavLink(
        root.$t("global.contact-support"),
        "add",
        () => {
          store.dispatch("modals/open", {
            template: ContactSupport,
            data: { department: root.$t("global.support") },
          });
        },
        false,
        null,
        "epsilon"
      ),
      new NavLink("Open", "open-page", "support", false, null, "epsilon"),
    ];

    const invoicesIcons = [new NavLink("Open", "open-page", "invoices", false, null, "epsilon")];

    const creditIcons = [new NavLink("Open", "open-page", "credits", false, null, "epsilon")];

    const statusIcons = [new NavLink("Open", "open-page", "system-status", false, null, "epsilon")];

    const client = computed(() => store.getters["auth/getClient"]);

    const billingAddress = computed(() => store.getters["addresses/getBillingAddress"]);
    const billingAddressStatus = computed(() => store.state.addresses.status);

    const showPin = ref(false);

    const pin = computed(() => {
      if (showPin.value) {
        return client.value.pin;
      }
      return "• • • • • •";
    });

    const toggleShowPin = () => {
      showPin.value = !showPin.value;
    };

    const getBillingStatus = () => {
      if (client.value.overdue > 0) {
        return "overdue";
      } else if (client.value.due > 0 && client.value.overdue === 0) {
        return "unpaid";
      } else {
        return "paid";
      }
    };

    const isOverdue = () => {
      return getBillingStatus() === "overdue";
    };

    const getPaymentClass = () => {
      return { overdue: "tau", unpaid: "upsilon", paid: "sigma" }[getBillingStatus()];
    };

    const editPin = () => {
      store.dispatch("modals/open", {
        template: PinEdit,
        data: {
          clientId: client.value.id,
          pin: client.value.pin,
        },
      });
    };

    const editDefaultPayment = () => {
      store.dispatch("modals/open", {
        template: DefaultPaymentEdit,
        data: {
          clientId: client.value.id,
          paymentType: client.value.paymentType,
        },
      });
    };

    onMounted(() => {
      store.dispatch("announcements/getList");
      store.dispatch("addresses/getList");

      // Temporarily Disabling Status Section due to CSP errors
      // getStatusPageSummary().then((summaryData) => {
      //   statusPageSummary.value = summaryData;
      // });

      showVerifyBillingTaxModal(client.value);
    });

    return {
      alerts,
      addressesIcons,
      announcements,
      announcementsIcons,
      billingAddress,
      callInPinIcons,
      clearPortalAlert,
      client,
      creditIcons,
      defaultPaymentIcons,
      editDefaultPayment,
      editPin,
      getBillingStatus,
      getPaymentClass,
      invoicesIcons,
      isOverdue,
      moneyFormat,
      pin,
      showPin,
      supportIcons,
      toggleShowPin,
      billingAddressStatus,
      // statusPageSummary, Temporarily Disabling Status Section due to CSP errors
      statusIcons,
    };
  },
};
</script>
<style lang="scss" scoped>
@import "~@/styles/variables.scss";

.pin-container {
  .iconToggle:hover {
    cursor: pointer;
  }
}
.status-page {
  ul {
    list-style-type: none;
    padding-left: 1rem;
    li {
      margin-bottom: 1rem;
    }
  }
  .card-body .toast {
    max-width: none;
    margin: -0.875rem -0.875rem 0.875rem -0.875rem;
    opacity: 1;
    box-shadow: none;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    .toast-header {
      padding: 0.875rem 0.875rem;
      border-left-width: 4px;
      border-left-style: solid;
    }
    .toast-icon {
      margin-right: 1.2rem;
    }
  }
}
</style>
